import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, type Warehouse } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, Checkbox, EmptyState, Field, Input, Notice, Spinner } from '../ui/controls'

/** Справочник складов со сводкой (п. 4.9, А-804). */
export default function WarehousesPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.warehouses

  const [list, setList] = useState<Warehouse[] | null>(null)
  const [editing, setEditing] = useState<Warehouse | 'new' | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async () => {
    try {
      setList(await api.warehouses())
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' })
      setList([])
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  if (list === null) return <Spinner label="Загрузка складов…" />

  return (
    <section className="max-w-5xl">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Склады</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Транзитные и сервисные склады участвуют в учёте, но покупателю не показываются.
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            + Склад
          </Button>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {list.length === 0 ? (
        <EmptyState
          title="Складов нет"
          hint="Заведите первый склад — без него нельзя оприходовать товар и вести остатки."
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => setEditing('new')}>
                Создать склад
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
          <table className="w-full min-w-[46rem] text-left text-sm">
            <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
              <tr>
                <th className="px-3 py-2.5 font-medium">Название</th>
                <th className="px-3 py-2.5 font-medium">Код</th>
                <th className="px-3 py-2.5 text-right font-medium">Позиций</th>
                <th className="px-3 py-2.5 text-right font-medium">Единиц</th>
                <th className="px-3 py-2.5 text-right font-medium">Стоимость</th>
                <th className="px-3 py-2.5 font-medium">Состояние</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
              {list.map((wh) => (
                <tr
                  key={wh.id}
                  onClick={() => setEditing(wh)}
                  className="cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <td className="px-3 py-2.5 text-ink-800 dark:text-ink-200">
                    {wh.name}
                    {wh.address && <div className="text-xs text-ink-400">{wh.address}</div>}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-ink-500">{wh.code}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{wh.positions}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{wh.units}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {wh.value.toLocaleString('ru-RU')} ₸
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {!wh.is_active && (
                        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600 dark:bg-ink-800">
                          отключён
                        </span>
                      )}
                      {wh.is_public ? (
                        <span className="rounded-full bg-success-100 dark:bg-success-700/25 px-2 py-0.5 text-xs text-success-700 dark:text-success-100">
                          на витрине
                        </span>
                      ) : (
                        <span className="rounded-full bg-warning-100 dark:bg-warning-700/25 px-2 py-0.5 text-xs text-warning-700 dark:text-warning-100">
                          служебный
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <WarehouseForm
          key={editing === 'new' ? 'new' : editing.id}
          warehouse={editing === 'new' ? undefined : editing}
          canWrite={canWrite}
          onSaved={async (text) => {
            setEditing(null)
            await reload()
            setNotice({ kind: 'success', text })
          }}
          onError={(text) => setNotice({ kind: 'error', text })}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  )
}

function WarehouseForm({
  warehouse,
  canWrite,
  onSaved,
  onError,
  onCancel,
}: {
  warehouse?: Warehouse
  canWrite: boolean
  onSaved: (text: string) => Promise<void>
  onError: (text: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(warehouse?.name ?? '')
  const [code, setCode] = useState(warehouse?.code ?? '')
  const [address, setAddress] = useState(warehouse?.address ?? '')
  const [isPublic, setIsPublic] = useState(warehouse?.is_public ?? true)
  const [isActive, setIsActive] = useState(warehouse?.is_active ?? true)
  const [pending, setPending] = useState(false)

  return (
    <div className="mt-5 max-w-xl rounded-lg border border-ink-200 p-4 dark:border-ink-800">
      <h3 className="mb-4 text-sm font-semibold text-ink-900 dark:text-ink-50">
        {warehouse ? 'Изменение склада' : 'Новый склад'}
      </h3>

      <div className="space-y-4">
        <Field label="Название">
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} />
        </Field>
        <Field label="Код" hint="Если оставить пустым, будет предложен по названию">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={!canWrite}
            className="font-mono"
          />
        </Field>
        <Field label="Адрес" hint="Необязательно">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} disabled={!canWrite} />
        </Field>
        <Checkbox
          label="Показывать на витрине"
          hint="Остатки склада попадают в публичные данные каталога"
          checked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
          disabled={!canWrite}
        />
        <Checkbox
          label="Активен"
          hint="Склад с ненулевыми остатками отключить нельзя"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          disabled={!canWrite}
        />
      </div>

      {canWrite && (
        <div className="mt-5 flex gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={pending || name.trim() === ''}
            onClick={async () => {
              setPending(true)
              try {
                const body = {
                  name: name.trim(),
                  code: code.trim(),
                  address: address.trim() || null,
                  is_public: isPublic,
                  is_active: isActive,
                }
                if (warehouse) {
                  await api.updateWarehouse(warehouse.id, body)
                  await onSaved('Сохранено')
                } else {
                  await api.createWarehouse(body)
                  await onSaved('Склад создан')
                }
              } catch (e) {
                if (e instanceof ApiError) {
                  const d = e.details as { positions?: number }
                  onError(
                    d.positions
                      ? `${e.message}. Позиций с остатком: ${d.positions}.`
                      : e.message,
                  )
                } else {
                  onError('Не удалось сохранить')
                }
              } finally {
                setPending(false)
              }
            }}
          >
            {pending ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button size="sm" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
        </div>
      )}
    </div>
  )
}
